import {
  PCP_CONTACT_ERROR,
  type PcpContactErrorKey,
  type PcpNameAndContactFieldErrors,
} from '@/utilities/pcpValidation'

/** Second person (Patient Portal / order placement) vs third person (Client kit registration). */
export type PcpCopyVoice = 'patient' | 'client'

export function pcpCopyVoice(isClientKitRegistration: boolean): PcpCopyVoice {
  return isClientKitRegistration ? 'client' : 'patient'
}

export const PCP_FORM_ERROR = {
  hasPcpRequired: 'has_pcp_required',
  needPcpHelpRequired: 'need_pcp_help_required',
} as const

export type PcpFormErrorKey = (typeof PCP_FORM_ERROR)[keyof typeof PCP_FORM_ERROR]

const PCP_CONTACT_ERROR_COPY: Record<PcpContactErrorKey, Record<PcpCopyVoice, string>> = {
  [PCP_CONTACT_ERROR.pcpNameRequired]: {
    patient: 'PCP Name is required',
    client: 'PCP Name is required',
  },
  [PCP_CONTACT_ERROR.faxRequired]: {
    patient: 'Enter a 10-digit fax number',
    client: 'Enter a 10-digit fax number',
  },
  [PCP_CONTACT_ERROR.faxOrAddressRequired]: {
    patient: "Enter a 10-digit fax number or your PCP's address",
    client: "Enter a 10-digit fax number or the patient's PCP address",
  },
  [PCP_CONTACT_ERROR.faxInvalid]: {
    patient: 'Enter a valid 10 or 11-digit fax number',
    client: 'Enter a valid 10 or 11-digit fax number',
  },
  [PCP_CONTACT_ERROR.streetRequired]: {
    patient: 'Street name is required',
    client: 'Street name is required',
  },
  [PCP_CONTACT_ERROR.cityRequired]: {
    patient: 'City is required',
    client: 'City is required',
  },
  [PCP_CONTACT_ERROR.stateRequired]: {
    patient: 'State is required',
    client: 'State is required',
  },
}

const PCP_FORM_ERROR_COPY: Record<PcpFormErrorKey, Record<PcpCopyVoice, string>> = {
  [PCP_FORM_ERROR.hasPcpRequired]: {
    patient: 'Please select whether you have a PCP',
    client: 'Please select whether the patient has a PCP',
  },
  [PCP_FORM_ERROR.needPcpHelpRequired]: {
    patient: 'Please let us know if you would like help finding a PCP',
    client: 'Please let us know if the patient would like help finding a PCP',
  },
}

export function pcpValidationErrorCopy(
  error: string | undefined,
  voice: PcpCopyVoice,
): string | undefined {
  if (!error) return undefined
  const contactCopy = PCP_CONTACT_ERROR_COPY[error as PcpContactErrorKey]
  if (contactCopy) return contactCopy[voice]
  const formCopy = PCP_FORM_ERROR_COPY[error as PcpFormErrorKey]
  if (formCopy) return formCopy[voice]
  return error
}

export function mapPcpNameAndContactErrors(
  errors: PcpNameAndContactFieldErrors,
  voice: PcpCopyVoice,
): PcpNameAndContactFieldErrors {
  const next: PcpNameAndContactFieldErrors = {}
  for (const key of Object.keys(errors) as Array<keyof PcpNameAndContactFieldErrors>) {
    const mapped = pcpValidationErrorCopy(errors[key], voice)
    if (mapped) next[key] = mapped
  }
  return next
}
